import { useState, useRef, useCallback, useEffect } from "react";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { getAgentSessionId } from "./agentSession";
import { useVoiceAgentLanguage } from "./useVoiceAgentLanguage";
import { useAgentWebhook } from "./useAgentWebhook";
import type { VoiceAgentState } from "./types";

type GetUserFn = () => Promise<{
  data: { user: { id: string } | null };
}>;

export function useVoiceAgent(options?: { getUser?: GetUserFn }) {
  const getUser =
    options?.getUser ?? (() => getSupabaseClient().auth.getUser());
  const [state, setState] = useState<VoiceAgentState>("idle");
  const [transcript, setTranscript] = useState("");
  const [response, setResponse] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const transcriptRef = useRef("");
  const sessionIdRef = useRef(getAgentSessionId());
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [voiceLanguage] = useVoiceAgentLanguage();
  const webhook = useAgentWebhook({ getUser });

  useEffect(() => {
    if (state === "processing") {
      setElapsedSeconds(0);
      timerRef.current = setInterval(() => {
        setElapsedSeconds((s) => s + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [state]);

  const isSupported =
    typeof window !== "undefined" &&
    ("SpeechRecognition" in window || "webkitSpeechRecognition" in window);

  const startRecording = useCallback(() => {
    if (!isSupported) return;

    const SpeechRecognitionAPI =
      (window as any).SpeechRecognition ??
      (window as any).webkitSpeechRecognition;

    const recognition: SpeechRecognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = voiceLanguage;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let full = "";
      for (let i = 0; i < event.results.length; i++) {
        full += event.results[i][0].transcript;
      }
      transcriptRef.current = full;
      setTranscript(full);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      setError(`Speech error: ${event.error}`);
      setState("error");
    };

    sessionIdRef.current = getAgentSessionId();

    recognitionRef.current = recognition;
    transcriptRef.current = "";
    setTranscript("");
    setResponse(null);
    setError(null);
    setState("recording");
    recognition.start();
  }, [isSupported, voiceLanguage]);

  const stopRecording = useCallback(async () => {
    recognitionRef.current?.stop();
    setState("processing");

    try {
      const message = await webhook.submit({
        text: transcriptRef.current,
        sessionId: sessionIdRef.current || getAgentSessionId(),
        language: voiceLanguage,
      });
      setResponse(message);
      setState("response");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed");
      setState("error");
    }
  }, [webhook, voiceLanguage]);

  const reset = useCallback(() => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    transcriptRef.current = "";
    // Keep sessionId so Redis memory survives sheet close / remount.
    setTranscript("");
    setResponse(null);
    setError(null);
    setState("idle");
  }, []);

  return {
    state,
    transcript,
    response,
    error,
    elapsedSeconds,
    isSupported,
    isSheetOpen: state !== "idle",
    startRecording,
    stopRecording,
    reset,
  };
}
